/**
 * PainelDeNotificacoes — as notificações do parceiro no Início (PR 5).
 *
 * Duas variantes da mesma peça:
 * - `coluna`: a coluna fixa da direita no computador. Lista inteira, "Ver mais"
 *   quando há próxima página; cabeçalho e rodapé ficam e só a lista rola
 *   (por isso é flex-col com `min-h-0` — o `InicioLayout` limita a altura).
 * - `compacta`: no celular, entre o atalho do Atendimento e os comunicados.
 *   Só as 3 mais recentes e "Ver todas".
 *
 * "Não lida" nunca é só cor: além do fundo verde-claro, o título fica em
 * negrito, há o ponto e um texto só para leitor de tela. Cada item é um
 * `<button>` de verdade (≥ 48 px) que entrega o item inteiro em `aoAbrir` —
 * quem marca como lida e navega é a página.
 *
 * `data-testid` do raiz muda com a variante (`inicio-notificacoes` /
 * `inicio-notificacoes-compactas`); os testids internos são os mesmos nas
 * duas — quem renderiza as duas ao mesmo tempo consulta com `within()`.
 *
 * Revisão de design (19/09/2026), medida no navegador com 27 notificações:
 * - B1 — o cabeçalho tem `min-h-12`, e um `min-height` explícito substitui o
 *   `min-height: auto` que impediria o flex-col de espremê-lo. Com a lista
 *   transbordando (20+ itens dentro do `max-h` da coluna), o flex reduzia o
 *   cabeçalho de 85 px pra 48 px e a 2ª linha ("Marcar todas como lidas")
 *   vazava POR CIMA da 1ª notificação — 28 px de sobreposição. `shrink-0`
 *   no cabeçalho e no rodapé. Com poucas notificações não acontecia, e foi
 *   por isso que o E2E (2 itens) não viu.
 * - S3 — "Ver menos" no fim de 27 itens recolhia a lista e deixava o `main`
 *   no mesmo scrollTop: o painel ia parar ~1.900 px acima da janela e o
 *   parceiro caía no meio dos Comunicados, com o foco no `<body>`. Agora o
 *   painel volta pra vista e o foco vai pro "Ver todas".
 * - S4 — em `lg` (1024–1279 px) o Início é coluna única; a compacta ocupava
 *   704 px de largura com 3 linhas de 85 px (~60 % vazias): 357 px de altura
 *   que empurravam o primeiro Comunicado pra baixo da dobra de 768 px. Em
 *   `lg` a compacta abre os itens em 3 colunas (some em `xl`, então só esse
 *   intervalo muda).
 * - P5 — na coluna (340 px), "Notificações [n]" + o rótulo de 193 px não
 *   cabem numa linha: o botão descia sozinho e o cabeçalho ficava com 85 px.
 *   Na coluna o botão é só ícone, com o rótulo em `sr-only` e `title`.
 */
import { Bell, CheckCheck, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { useId, useLayoutEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { PortalNotification } from "@/services/portalApi";

import { LIMITE_COMPACTO } from "./constantes";
import { EstadoDaSecao } from "./EstadoDaSecao";
import { dataHoraCompleta, tempoRelativo } from "./tempoRelativo";

export interface PainelDeNotificacoesProps {
  variante: "coluna" | "compacta";
  itens: PortalNotification[];
  carregando?: boolean;
  erro?: boolean;
  aoTentarDeNovo?: () => void;
  /** Há mais notificações além das listadas (próxima página). */
  temMais?: boolean;
  /** "Ver mais" (coluna) / "Ver todas" (compacta). */
  aoVerMais?: () => void;
  carregandoMais?: boolean;
  aoAbrir: (item: PortalNotification) => void;
  aoMarcarTodas?: () => void;
  marcandoTodas?: boolean;
  /** Contagem real de não lidas (a API tem o número; não é `itens.filter`). */
  naoLidas?: number;
}

export function PainelDeNotificacoes({
  variante,
  itens,
  carregando = false,
  erro = false,
  aoTentarDeNovo,
  temMais = false,
  aoVerMais,
  carregandoMais = false,
  aoAbrir,
  aoMarcarTodas,
  marcandoTodas = false,
  naoLidas = 0,
}: PainelDeNotificacoesProps) {
  const idTitulo = useId();
  const compacta = variante === "compacta";
  // Fix round 1 — a compacta ficava travada em 3 pra sempre: "Ver todas" só
  // subia o LIMITE DE BUSCA no servidor (`aoVerMais`), mas a fatia local
  // (`itens.slice(0, LIMITE_COMPACTO)`) continuava cortando em 3 de qualquer
  // jeito — clicar mostrava o spinner e nada mudava na tela (botão morto).
  // `expandida` é local: o primeiro clique revela o que JÁ está em `itens`
  // (sem esperar rede nenhuma); dali em diante a compacta se comporta como a
  // coluna (mesma paginação de verdade via `temMais`).
  const [expandida, setExpandida] = useState(false);
  const mostrarTudo = !compacta || expandida;
  const visiveis = mostrarTudo ? itens : itens.slice(0, LIMITE_COMPACTO);
  const temLista = !carregando && !erro && visiveis.length > 0;
  // Compacta ainda recolhida: "Ver todas" aparece se sobrou alguém de fora
  // (aqui ou no servidor). Depois de expandida (ou a coluna, que já nasce
  // "mostrarTudo"): "Ver mais" só quando o servidor ainda tem página.
  const mostrarVerMais =
    temLista && !!aoVerMais && (mostrarTudo ? temMais : itens.length > LIMITE_COMPACTO || temMais);
  // M12 (revisão final) — a compacta EXPANDIDA pode recolher de novo: com
  // 20+ notificações carregadas, "Ver todas" empurrava os Comunicados pra
  // muito longe no celular, sem jeito de voltar sem recarregar a página.
  const mostrarVerMenos = compacta && expandida && temLista;
  const mostrarRodape = mostrarVerMais || mostrarVerMenos;

  // S3 (revisão de design) — quem recolheu precisa VER o painel recolhido e
  // continuar com o foco nele. `recolheuRef` distingue "recolhida pelo
  // clique" da montagem (que também nasce com `expandida=false`): só o clique
  // rola e foca. `useLayoutEffect` porque a rolagem tem de acontecer ANTES
  // da pintura — com `useEffect` haveria um quadro com o parceiro largado no
  // meio dos Comunicados.
  const secaoRef = useRef<HTMLElement>(null);
  const verTodasRef = useRef<HTMLButtonElement>(null);
  const recolheuRef = useRef(false);

  useLayoutEffect(() => {
    if (expandida || !recolheuRef.current) return;
    recolheuRef.current = false;
    secaoRef.current?.scrollIntoView({ block: "start" });
    // `preventScroll`: a seção já rolou pro lugar; focar com rolagem puxaria
    // o "Ver todas" (no rodapé) pro topo da janela e escondia o cabeçalho.
    verTodasRef.current?.focus({ preventScroll: true });
  }, [expandida]);

  function clicarVerMais() {
    if (carregandoMais) return;
    // Expande ANTES de (também) pedir mais ao servidor — o efeito local é
    // instantâneo, não depende da resposta da rede.
    if (compacta && !expandida) setExpandida(true);
    // I1 (revisão final) — só busca no servidor se HOUVER próxima página.
    // Antes disto, a compacta chamava `aoVerMais()` incondicionalmente: com
    // tudo já carregado (`temMais=false`), o clique ainda disparava
    // `fetchNextPage`/trocava a query, e a lista piscava pro esqueleto e
    // voltava do mesmo tamanho — um "Ver todas" que parecia recarregar à toa.
    if (temMais) aoVerMais?.();
  }

  function clicarVerMenos() {
    recolheuRef.current = true;
    setExpandida(false);
  }

  return (
    <section
      ref={secaoRef}
      aria-labelledby={idTitulo}
      data-testid={compacta ? "inicio-notificacoes-compactas" : "inicio-notificacoes"}
      // D3 (desenho do portal 0.6): alvo do "Ver todas" do sino (`rolarAteAsNotificacoes`). Marca, não `id`: as DUAS variantes
      // ficam no DOM ao mesmo tempo e `id` repetido é inválido.
      data-alvo-do-hash="notificacoes"
      // `scroll-mt-4`: o `main` do PortalLayout é quem rola; ao voltar pra
      // vista (S3) o painel para 16 px abaixo do topo, não colado nele.
      className="flex min-h-0 scroll-mt-4 flex-col rounded-lg border border-emerald-100/80 bg-white/80 shadow-sm backdrop-blur-sm"
    >
      {/* `flex-wrap`: na compacta, título + contagem + "Marcar todas como
          lidas" não cabem em 326 px (celular); o botão desce para uma segunda
          linha, alinhada à direita, em vez de vazar do cartão. `shrink-0` é
          o B1 (ver o comentário do módulo). */}
      <div className="flex min-h-12 shrink-0 flex-wrap items-center justify-between gap-x-2 gap-y-1 border-b border-emerald-50 px-4 py-2">
        <h2
          id={idTitulo}
          // S5 — `font-bold tracking-tight`: a mesma voz do h1 e do h2
          // "Comunicados"; o tamanho fica em 16 px porque a coluna tem 340 px.
          className="flex items-center gap-2 text-base font-bold tracking-tight text-emerald-900"
        >
          <Bell className="h-5 w-5 text-emerald-600" aria-hidden="true" />
          Notificações
          {naoLidas > 0 && (
            <>
              <span
                aria-hidden="true"
                className="rounded-full bg-emerald-600 px-2 py-0.5 text-xs font-semibold text-white"
              >
                {naoLidas > 99 ? "99+" : naoLidas}
              </span>
              <span className="sr-only">, {naoLidas} não lidas</span>
            </>
          )}
        </h2>
        {naoLidas > 0 && aoMarcarTodas && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={aoMarcarTodas}
            disabled={marcandoTodas}
            data-testid="notificacoes-marcar-todas"
            // P5 — só na coluna: o rótulo vira `sr-only` e o `title` faz as
            // vezes de tooltip pra quem usa mouse.
            title={compacta ? undefined : "Marcar todas como lidas"}
            // M7 (revisão final) — 48 px no celular (era 40 px nas duas
            // larguras); no desktop volta a 40 px, mesmo padrão das ações
            // secundárias do cartão do comunicado (`h-12 sm:h-10`).
            // P4 — rótulo de 14 px no celular (era 12 px num alvo de 48 px,
            // ao lado de um "Ver todas" de 14 px); 12 px só a partir de `sm`.
            className={cn(
              "ml-auto h-12 shrink-0 gap-1.5 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800 sm:h-10",
              compacta ? "px-2 text-sm sm:text-xs" : "w-10 px-0"
            )}
          >
            {marcandoTodas ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <CheckCheck className="h-4 w-4" aria-hidden="true" />
            )}
            <span className={compacta ? undefined : "sr-only"}>Marcar todas como lidas</span>
          </Button>
        )}
      </div>

      <div className={cn("min-h-0", !compacta && "flex-1 overflow-y-auto")}>
        {carregando ? (
          <div className="px-4 py-3">
            <EstadoDaSecao
              tipo="carregando"
              mensagem="Carregando notificações…"
              compacto={compacta}
            />
          </div>
        ) : erro ? (
          <div className="p-3">
            <EstadoDaSecao
              tipo="erro"
              mensagem="Não foi possível carregar as notificações."
              aoTentarDeNovo={aoTentarDeNovo}
            />
          </div>
        ) : visiveis.length === 0 ? (
          <EstadoDaSecao
            tipo="vazio"
            mensagem="Nenhuma notificação por enquanto."
            detalhe="Quando algo novo chegar para você, aparece aqui."
            icone={<Bell />}
            compacto={compacta}
          />
        ) : (
          <ul
            className={cn(
              "divide-y divide-emerald-50",
              // S4 — 3 colunas só em `lg` (1024–1279 px), só na compacta
              // (que some em `xl`; a coluna é outra peça). `divide-x` troca a
              // linha entre itens de horizontal pra vertical.
              compacta && "lg:grid lg:grid-cols-3 lg:divide-x lg:divide-y-0"
            )}
          >
            {visiveis.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => aoAbrir(item)}
                  data-testid="notificacao-item"
                  data-lida={item.is_read ? "sim" : "nao"}
                  className={cn(
                    "flex min-h-12 w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-emerald-50/70 focus-visible:bg-emerald-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-500",
                    // S4 — na grade de 3 colunas, o botão preenche a célula
                    // (o `<li>` estica; o alvo de toque também).
                    compacta && "lg:h-full",
                    !item.is_read && "bg-emerald-50/50"
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      "mt-1.5 h-2 w-2 shrink-0 rounded-full",
                      item.is_read ? "bg-transparent" : "bg-emerald-600"
                    )}
                  />
                  <span className="min-w-0 flex-1">
                    {!item.is_read && <span className="sr-only">Não lida: </span>}
                    <span
                      className={cn(
                        "line-clamp-2 text-sm leading-snug",
                        item.is_read ? "font-medium text-slate-700" : "font-semibold text-slate-900"
                      )}
                    >
                      {item.title}
                    </span>
                    {item.body && (
                      // P3 — um degrau abaixo do título (13 px, slate-500):
                      // no item lido, título 14/500 e corpo 14/400 liam como
                      // duas linhas iguais.
                      <span className="mt-0.5 line-clamp-2 text-[13px] leading-snug text-slate-500">
                        {item.body}
                      </span>
                    )}
                    <time
                      dateTime={item.created_at}
                      title={dataHoraCompleta(item.created_at)}
                      className="mt-1 block text-xs text-slate-500"
                    >
                      {tempoRelativo(item.created_at)}
                    </time>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {mostrarRodape && (
        <div className="flex shrink-0 border-t border-emerald-50">
          {mostrarVerMais && (
            <Button
              ref={verTodasRef}
              type="button"
              variant="ghost"
              onClick={clicarVerMais}
              // I1 (revisão final) — `aria-disabled`, não `disabled`: o nativo
              // tira o botão da árvore de foco NA HORA, e quem clicou com o
              // teclado perde o foco pro `<body>` no meio da lista.
              // `clicarVerMais` já ignora cliques enquanto `carregandoMais`
              // (guarda acima).
              aria-disabled={carregandoMais}
              data-testid="notificacoes-ver-mais"
              className={cn(
                "h-12 flex-1 justify-center gap-1.5 rounded-t-none text-sm text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800 sm:h-12",
                mostrarVerMenos && "border-r border-emerald-50"
              )}
            >
              {carregandoMais ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  Carregando…
                </>
              ) : (
                <>
                  {mostrarTudo ? "Ver mais" : "Ver todas"}
                  <ChevronDown className="h-4 w-4" aria-hidden="true" />
                </>
              )}
            </Button>
          )}
          {/* M12 (revisão final) — só existe na compacta EXPANDIDA: recolhe de
              volta pras 3 mais recentes, sem depender de recarregar a
              página. Ao lado de "Ver mais" quando os dois cabem (o servidor
              ainda tem página); sozinho e cheio quando não há mais nada a
              buscar. */}
          {mostrarVerMenos && (
            <Button
              type="button"
              variant="ghost"
              onClick={clicarVerMenos}
              data-testid="notificacoes-ver-menos"
              className="h-12 flex-1 justify-center gap-1.5 rounded-t-none text-sm text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800 sm:h-12"
            >
              Ver menos
              <ChevronUp className="h-4 w-4" aria-hidden="true" />
            </Button>
          )}
        </div>
      )}
    </section>
  );
}

export default PainelDeNotificacoes;
